/**
 * Feature flags for Early Literacy App.
 *
 * Each flag is a static default that may be overridden by an environment
 * variable at build time. Environment variable takes precedence when set.
 *
 * To enable a flag in production, either:
 *   1. Change the default below to `true` and redeploy, OR
 *   2. Set the corresponding REACT_APP_* env var to 'true' in the deploy
 *      environment (GitHub Actions secrets / .env.production)
 *
 * Flags should be removed from this file once the feature is fully
 * launched — don't leave permanent feature flags lying around.
 */

const parseFlag = (envValue, defaultValue) => {
  if (envValue === undefined || envValue === null || envValue === '') {
    return defaultValue;
  }
  return envValue === 'true' || envValue === '1';
};

export const FEATURES = {
  // benchmarkScreener: Controls visibility of the Nonsense Word Fluency (NWF)
  // benchmark screener. LIVE in every deployed environment as of v3.68.0
  // (Kemal-authorized 2026-08-22): prod (firebase-hosting-merge.yml), dev
  // (firebase-hosting-dev.yml), PR previews
  // (firebase-hosting-pull-request.yml), demo (.env.demo), and the QA
  // harness (e2e/mobile-qa/e2e.env). The default below stays false so a
  // plain local build is unaffected (TLS/BOY precedent); set the env var to
  // work on the feature locally. (The section held a second test,
  // first_sound "Onset Fluency", until v3.67.0 — retired because the
  // standalone Phonemic Awareness screener assesses that skill.)
  //
  // With this flag OFF:
  //   - The /screener/assessment and /screener/results routes don't exist
  //   - The "Nonsense Word Fluency" sidebar section doesn't render
  //   - The section's Assessment Settings tab doesn't render
  //
  // Env var: REACT_APP_ENABLE_BENCHMARK_SCREENER
  benchmarkScreener: parseFlag(
    process.env.REACT_APP_ENABLE_BENCHMARK_SCREENER,
    false
  ),

  // paScreener: The Phonemic Awareness screener (4K phonological awareness)
  // — one five-step instrument (Rhyme Recognition, Rhyme Production,
  // Blending Syllables, Segmenting Syllables, Initial Sound Isolation)
  // administered 1:1 in a single sitting, with three parallel forms
  // (Sept / Jan / May).
  //
  // DELIBERATELY ITS OWN FLAG, not benchmarkScreener. Same posture (ON in
  // demo + the QA harness, absent/false in prod) but a separate switch:
  // the NWF benchmark screener and this are different instruments for
  // different grades, and the Elmbrook pilot may need the 4K screener on
  // for one district's 4K teachers WITHOUT turning the benchmark screener
  // on for anyone. Sharing a flag would make that impossible.
  //
  // With this flag OFF:
  //   - The /pa/screener and /pa/results routes don't exist
  //   - The "Phonemic Awareness" sidebar section doesn't render
  //   - No pa_* assessment can be created; existing docs are inert data
  //
  // To turn it on for an environment, set the env var in that
  // environment's layer (e.g. REACT_APP_ENABLE_PA_SCREENER: 'true' in the
  // relevant workflow's verify job, or .env.demo) — the same mechanism
  // benchmarkScreener uses.
  //
  // Env var: REACT_APP_ENABLE_PA_SCREENER
  paScreener: parseFlag(
    process.env.REACT_APP_ENABLE_PA_SCREENER,
    false
  ),

  // pa4kAssessments: The "Phonological Awareness (4K)" assessment group —
  // six short standard-pattern 1:1 assessments (Word Awareness, Rhyme
  // Recognition, Alliteration, Syllable Blending & Counting, Onset-Rime
  // Matching, Beginning Sound Isolation), each 5 items scored 0–5 with the
  // Mastery/Emerging/Needs Support bands, following the Letter/Sound
  // pattern (quarter-stamped, retakes allowed, no windows/forms).
  //
  // DELIBERATELY ITS OWN FLAG, not paScreener: the five-step "Phonemic
  // Awareness" screener is a different instrument (step container, parallel
  // forms, discontinue rule) with its own rollout timeline. DEV-ONLY
  // posture (Kemal-authorized 2026-08-31): ON in the dev + PR-preview
  // workflows and the QA harness (e2e.env); absent/false in prod
  // (firebase-hosting-merge.yml) AND demo (.env.demo) — note this is
  // narrower than paScreener, which demo does set.
  //
  // With this flag OFF:
  //   - The /pa4k/assessment and /pa4k/results routes don't exist
  //   - The "Phonological Awareness (4K)" sidebar section doesn't render
  //   - No pa4k_* assessment can be created; existing docs are inert data
  //
  // Env var: REACT_APP_ENABLE_PA4K_ASSESSMENTS
  pa4kAssessments: parseFlag(
    process.env.REACT_APP_ENABLE_PA4K_ASSESSMENTS,
    false
  ),

  // paDiagnostic: The "Phonemic Awareness Diagnostic" (PA-D) — an
  // auditory-only, teacher-administered diagnostic with four grade tiers
  // (4K / Kindergarten / Grade 1 / Grades 2-3), a keyboard-driven Teacher
  // HUD, and item-level results. A FULLY ISOLATED module: everything lives
  // in src/pa-diagnostic/ behind its index.js surface; testType
  // pa_diagnostic, routes /pa-diagnostic/*. A THIRD instrument alongside
  // the "Phonemic Awareness" screener and the "Phonological Awareness (4K)"
  // group — it touches neither.
  //
  // Posture: ON in the dev + PR-preview workflow verify steps and the QA
  // harness (e2e.env) since v3.72.0 (dev-only walkthrough, Kemal-approved
  // 2026-09-02). Prod (firebase-hosting-merge.yml): ON from v3.74.0
  // (Kemal-authorized 2026-09-10) to v3.84.10; the line is set to 'false'
  // since v3.84.11 (Kemal decision 2026-09-28) pending Elmbrook Teaching &
  // Learning — see BACKLOG "PA-D ON PROD". Still absent in demo
  // (.env.demo). The default below stays false so a plain local build is
  // unaffected.
  //
  // With this flag OFF:
  //   - The /pa-diagnostic/* routes don't exist
  //   - The "Phonemic Awareness Diagnostic" sidebar section doesn't render
  //   - No pa_diagnostic assessment can be created; existing docs are inert
  //
  // Env var: REACT_APP_ENABLE_PA_DIAGNOSTIC
  paDiagnostic: parseFlag(
    process.env.REACT_APP_ENABLE_PA_DIAGNOSTIC,
    false
  ),

  // vowelTests: KILL SWITCH ONLY for the two optional Letter/Sound tests
  // added in v3.73.0 — Long Vowels and Short Vowels (src/constants/
  // vowelTests.js). DEFAULT TRUE when the env var is unset, so no workflow
  // or .env change is needed for the tests to be on everywhere (dev, PR
  // previews, prod, demo, the QA harness). Set the env var to 'false' to
  // pull them.
  //
  // With this flag OFF:
  //   - The Letter/Sound test picker offers exactly Uppercase / Lowercase /
  //     Sounds (no vowel steps)
  //   - Results shows no Long Vowels / Short Vowels columns or detail block,
  //     the Report Card / Print show no vowel rows, the CSV export carries
  //     no vowel rows, and Small Groups offers no vowel grouping option
  //   - Existing long_vowels / short_vowels docs are inert data
  //
  // Env var: REACT_APP_ENABLE_VOWEL_TESTS
  vowelTests: parseFlag(
    process.env.REACT_APP_ENABLE_VOWEL_TESTS,
    true
  ),

  // soundsVariants: the Sounds test given in lowercase, in both cases, or on
  // the 21 consonants only (v3.85.0, src/utils/assessmentEntry.js row
  // table). Dark on prod until Vesile's demo walk — ON for dev
  // (firebase-hosting-dev.yml), PR previews (firebase-hosting-pull-
  // request.yml), the QA harness (e2e/mobile-qa/e2e.env) and demo
  // (.env.demo, added by hand on deploy); OFF in production
  // (firebase-hosting-merge.yml carries the line set to 'false' so the flip
  // is a one-word diff). Pinned by src/utils/soundsVariantsFlag.test.js.
  //
  // With this flag OFF:
  //   - The Test Type pickers offer exactly the v3.84.10 rows (the Sounds row
  //     keeps its "Sounds" / "Letter Sounds" label)
  //   - Letter Card Sans is not offered in the Assessment Font list
  //   - The vowel cards keep "A a" (no case inheritance), Practice keeps
  //     Uppercase as its default
  //   - Letter/Sound docs are written exactly as before (no letterCase /
  //     notAdministered fields)
  // Unflagged, no-ops on existing data: uniform card sizing, the percent
  // band cuts, case-aware readers, the case tag, greyed not-administered cells.
  //
  // Env var: REACT_APP_ENABLE_SOUNDS_VARIANTS
  soundsVariants: parseFlag(
    process.env.REACT_APP_ENABLE_SOUNDS_VARIANTS,
    false
  ),

  // boyReadiness: The optional "Beginning of Year" readiness section — three
  // once-per-school-year flashcard readiness checks (Geometric Shapes, Numbers 1–10,
  // Colors). LIVE in every deployed environment as of v3.55.0: prod
  // (firebase-hosting-merge.yml), dev (firebase-hosting-dev.yml), demo
  // (.env.demo), and the QA harness (e2e/mobile-qa/e2e.env). The default
  // below stays false so a plain local build is unaffected (TLS precedent);
  // set the env var to work on the feature locally.
  //
  // With this flag OFF:
  //   - The /boy/readiness route doesn't exist
  //   - The "4K Readiness" sidebar section doesn't render
  //   - The section's Assessment Settings toggle row doesn't render
  //   - The BOY section of the teacher print report doesn't render
  //
  // With the flag ON, visibility is the teacher's call: the group is VISIBLE
  // by default (sidebarGroupsHidden is empty/absent) and hiding it in
  // Assessment Settings removes both the nav item and the report section —
  // this group's one deliberate report-side effect (src/utils/boyReport.js).
  //
  // Env var: REACT_APP_ENABLE_BOY_READINESS
  boyReadiness: parseFlag(
    process.env.REACT_APP_ENABLE_BOY_READINESS,
    false
  ),

  // tlsRole: Controls visibility of the TLS (Teaching Learning Specialist)
  // school-level role and the Skill Gap Finder. Pilot at Swanson Elementary
  // (Elmbrook School District); ON in production since v3.68-era workflows
  // (firebase-hosting-merge.yml sets it) — the default here stays false.
  //
  // With this flag OFF:
  //   - The /school/* routes don't exist
  //   - The "School" sidebar section doesn't render
  //   - The TLS option doesn't appear in the Admin role selector
  //   - The "Pull-out groups from your TLS" section in /groups stays hidden
  //
  // Env var: REACT_APP_ENABLE_TLS_ROLE
  tlsRole: parseFlag(
    process.env.REACT_APP_ENABLE_TLS_ROLE,
    false
  ),

  // schoolView: School View (v3.78.0) — the TLS / global-admin building page
  // (students grouped by class or by grade) and the Grade Report under
  // /school/view. Requires tlsRole (the School group). ON everywhere since
  // v3.78.2 (dev, PR previews, demo, the QA harness AND production — the
  // merge workflow sets it); the source default stays false.
  //
  // With this flag OFF:
  //   - /school/view and /school/view/:schoolId/grade/:gradeSlug don't exist
  //     (they fall through to the role landing redirect like any unknown path)
  //   - The "School View" sidebar entry doesn't render (TLS or admin)
  //   - No read is issued — the loader is only reachable from those routes
  //
  // Env var: REACT_APP_ENABLE_SCHOOL_VIEW
  schoolView: parseFlag(
    process.env.REACT_APP_ENABLE_SCHOOL_VIEW,
    false
  ),

  // reports: The teacher-facing "Reports" sidebar group and its first entry,
  // Class Report (/reports/class, v3.79.0) — every student in the signed-in
  // teacher's own classes × every assessment family on one page, a Mastery
  // level | Latest score toggle, a window selector, print and CSV — fed by
  // the teacher's existing class-scoped reads (the Results shapes; no rules
  // change, no index). Admission = exactly the teacher tabs (teacher, EA,
  // legacy role-less accounts — src/utils/teacherTabs.js), gated through
  // canSeeReports (src/utils/reportsGate.js) for the route AND the nav.
  //
  // Posture: ON in the dev + PR-preview workflow verify steps, the QA
  // harness (e2e.env), demo (.env.demo, the owner's git-ignored laptop file)
  // and — since v3.82.1 (Kemal-authorized 2026-09-24) — prod
  // (firebase-hosting-merge.yml). The default below stays false so a plain
  // local build is unaffected (TLS/BOY/NWF/PA-D precedent).
  //
  // With this flag OFF:
  //   - The /reports/class route doesn't exist (it falls through to the role
  //     landing redirect like any unknown path)
  //   - The "Reports" sidebar group doesn't render
  //   - No read is issued — the loader is only reachable from that route
  //
  // Env var: REACT_APP_ENABLE_REPORTS
  reports: parseFlag(
    process.env.REACT_APP_ENABLE_REPORTS,
    false
  ),

  // gradeTeam: Reports → Grade Team (v3.80.0) — a teacher at a building
  // where a global admin turned schools/{id}.settings.gradeTeamView ON sees
  // every class in HER grade at HER building as counts only (own class
  // pinned and drill-able, a grade-totals block). Peer classes come from the
  // schoolViewSummary callable as de-identified score histograms banded on
  // the client; no Firestore rule is widened, no new role or claim. Gate:
  // canSeeGradeTeam (src/utils/gradeTeamGate.js) = this flag + the teacher
  // claim + a qualifying building from the session probe
  // (src/utils/gradeTeamSource.js). Two layers, both required: the flag
  // (per environment) and the per-building setting (per school, by an admin).
  //
  // Posture: ON in the dev + PR-preview workflow verify steps and the QA
  // harness (e2e.env); ABSENT in prod (firebase-hosting-merge.yml) until the
  // owner flips it (BACKLOG Parked "Turn on Grade Team for prod teachers").
  // NEVER in .env.demo: the demo project has no Cloud Functions, so the callable
  // cannot exist there (BACKLOG "GRADE TEAM ON DEMO"). The default below stays
  // false so a plain local build is unaffected.
  //
  // With this flag OFF:
  //   - The /reports/grade-team route doesn't exist (it falls through to the
  //     role landing redirect like any unknown path)
  //   - The Reports → Grade Team entry doesn't render; no probe read is issued
  //   - The callable stays deployed but nothing in the app calls it
  //
  // Env var: REACT_APP_ENABLE_GRADE_TEAM
  gradeTeam: parseFlag(
    process.env.REACT_APP_ENABLE_GRADE_TEAM,
    false
  ),

  // aiInsightsEnabled: Reserved for future use — all AI UI removed in v3.16.0.
  // Flag kept so any future re-introduction has a consistent gate; default
  // remains OFF and there is no UI consumer at the moment.
  //
  // Env var: REACT_APP_ENABLE_AI_INSIGHTS
  aiInsightsEnabled: parseFlag(
    process.env.REACT_APP_ENABLE_AI_INSIGHTS,
    false
  ),

  // savedReportCards: The "Saved Report Cards" class-level overview section at
  // the bottom of the Results page. Hidden per Kemal (2026-07-14) — not needed
  // for now. The component (SavedReportCardsSection) and all its wiring in
  // Results.js are kept intact; flip this flag to bring the section back.
  //
  // Env var: REACT_APP_ENABLE_SAVED_REPORT_CARDS
  savedReportCards: parseFlag(
    process.env.REACT_APP_ENABLE_SAVED_REPORT_CARDS,
    false
  ),

  // onboardingTour: First-login onboarding (v3.75.0) — the welcome tour of
  // the teacher dashboard (driver.js, auto-started once per user per
  // TOUR_VERSION, replayable from Help → Take the tour), the two one-time
  // contextual hints (Letter/Sound card, Results) and the onboarding
  // user-doc writes they record. Everything lives in src/onboarding/ behind
  // OnboardingProvider. The How-to videos + Contact support Help items are
  // NOT this flag's since the split below (helpVideos).
  //
  // Posture: ON in dev + PR previews (workflow verify steps), demo
  // (.env.demo) and the QA harness (e2e.env) since v3.75.0 (Kemal-approved
  // 2026-09-13); ON in prod since v3.76.2 (firebase-hosting-merge.yml,
  // Kemal-authorized 2026-09-14). The default below stays false so a plain
  // local build is unaffected.
  //
  // With this flag OFF:
  //   - the tour hook never mounts: no auto-start, no hints, no Take the
  //     tour item, no user-doc onboarding write
  //   - data-tour attributes stay in the DOM but nothing targets them
  //   - the User Guide "Help menu" section drops its tour bullet
  //
  // Env var: REACT_APP_ENABLE_ONBOARDING_TOUR
  onboardingTour: parseFlag(
    process.env.REACT_APP_ENABLE_ONBOARDING_TOUR,
    false
  ),

  // helpVideos: How-to videos in the Help menu (split out of onboardingTour
  // in v3.76.0 so the videos could ship to prod first). Gates the
  // sidebar Help items "How-to videos" (hidden once the catalog is known to
  // be empty) and "Contact support", the on-demand appConfig/helpVideos
  // read (Help-group expand), the HelpVideosPanel / HelpVideoPlayer
  // mounting, and the User Guide "Help menu" bullets about videos.
  //
  // Posture: ON in prod (firebase-hosting-merge.yml), dev + PR previews
  // (workflow verify steps), demo (.env.demo) and the QA harness (e2e.env).
  // The default below stays false so a plain local build is unaffected
  // (TLS/BOY/NWF/PA-D precedent).
  //
  // With this flag OFF:
  //   - appConfig/helpVideos is never read; no panel, no player, no iframe
  //   - the Help group shows no How-to videos / Contact support items
  //   - with onboardingTour also off the Help group is exactly User Guide ·
  //     What's New · Send Feedback
  //
  // Env var: REACT_APP_ENABLE_HELP_VIDEOS
  helpVideos: parseFlag(
    process.env.REACT_APP_ENABLE_HELP_VIDEOS,
    false
  ),

  // demoMode: Marks a build as the public DEMO environment (k-literacy-demo).
  // ON only in the demo build (.env.demo sets REACT_APP_DEMO_MODE=true); OFF in
  // dev and prod. Drives demo-only UI: the DEMO banner, the single-login role
  // switcher (teacher/TLS — Admin is not a previewable view), and the email-only
  // login (no Google).
  //
  // Env var: REACT_APP_DEMO_MODE
  demoMode: parseFlag(
    process.env.REACT_APP_DEMO_MODE,
    false
  ),
};

// Convenience boolean for the demo environment.
export const IS_DEMO = FEATURES.demoMode;

/**
 * Convenience helper for conditional rendering.
 *   import { isFeatureEnabled } from '../constants/featureFlags';
 *   if (isFeatureEnabled('benchmarkScreener')) { ... }
 */
export const isFeatureEnabled = (flagName) => {
  if (!(flagName in FEATURES)) {
    console.warn(`[featureFlags] Unknown flag: ${flagName}`);
    return false;
  }
  return FEATURES[flagName];
};
